/*!
 * Button Click tracking for Snowplow v4.8.0 (https://github.com/snowplow/snowplow-javascript-tracker)
 * Copyright 2022 Snowplow Analytics Ltd, 2010 Anthon Pang
 * Licensed under BSD-3-Clause
 */

import { dispatchToTrackersInCollection } from '@snowplow/browser-tracker-core';
import { buildSelfDescribingEvent, removeEmptyProperties, resolveDynamicContext } from '@snowplow/tracker-core';

/**
 * Convert any {@link Filter} or undefined into a {@link FilterFunction}
 *
 * @param filter - The filter to convert
 * @returns The filter function
 */
function filterFunctionFromFilter(filter) {
    // If a function is provided, return it as-is
    if (typeof filter === 'function') {
        return filter;
    }
    if (typeof filter === 'object') {
        // If an allowlist is provided, return a function which matches the allowlist
        if ('allowlist' in filter) {
            return function (element) {
                var classes = Array.from((element === null || element === void 0 ? void 0 : element.classList) || []);
                return classes.some(function (className) { return filter.allowlist.includes(className); });
            };
        }
        // If a denylist is provided, return a function which matches the denylist
        if ('denylist' in filter) {
            return function (element) {
                var classes = Array.from((element === null || element === void 0 ? void 0 : element.classList) || []);
                return !classes.some(function (className) { return filter.denylist.includes(className); });
            };
        }
    }
    // Return a function that will match all buttons if no filter is provided
    return function () { return true; };
}
/**
 * Creates a {@link ButtonClickEvent} from a button element
 *
 * @param button - The button element to create the event from
 * @returns The button click event
 */
function createEventFromButton(button, defaultLabel) {
    var ret = {};
    if (button.tagName === 'INPUT') {
        ret.label = button.dataset.spButtonLabel || button.value;
    }
    else {
        ret.label =
            button.dataset.spButtonLabel ||
                button.innerText ||
                (typeof defaultLabel === 'function' ? defaultLabel(button) : defaultLabel) ||
                '(empty)';
    }
    button.id && (ret.id = button.id);
    button.name && (ret.name = button.name);
    button.classList.length && (ret.classes = Array.from(button.classList));
    return ret;
}

/**
 * Build a Button Click Event
 * Used when a user clicks on a <button> tag on a webpage
 *
 * @param event - Contains the properties for the Button Click event
 * @returns PayloadBuilder to be sent to {@link @snowplow/tracker-core#TrackerCore.track}
 */
function buildButtonClick(event) {
    var label = event.label, id = event.id, classes = event.classes, name = event.name;
    var eventJson = {
        schema: 'iglu:com.snowplowanalytics.snowplow/button_click/jsonschema/1-0-0',
        data: removeEmptyProperties({ label: label, id: id, classes: classes, name: name }),
    };
    return buildSelfDescribingEvent({ event: eventJson });
}

var _trackers = {};
// The event listeners added to the document, for each tracker
// This allows them to be removed with `removeEventListener` if button click tracking is disabled
var _listeners = {};
/**
 * Button click tracking
 *
 * Will automatically tracking button clicks once enabled with 'enableButtonClickTracking'
 * or you can manually track button clicks with 'trackButtonClick'
 */
function ButtonClickTrackingPlugin() {
    return {
        activateBrowserPlugin: function (tracker) {
            _trackers[tracker.id] = tracker;
        },
    };
}
/**
 * Manually log a click
 *
 * @param event - The event information
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackButtonClick(event, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildButtonClick(event), event.context, event.timestamp);
    });
}
/**
 * Enable automatic click tracking for all `<button>` and `<input type="button">` elements on the page
 *
 * @param configuration  - The configuration for automatic button click tracking
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function enableButtonClickTracking(configuration, trackers) {
    if (configuration === void 0) { configuration = {}; }
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    // Ensure that click tracking uses the latest configuration
    // In the case of `enableButtonClickTracking` being called multiple times in a row
    disableButtonClickTracking(trackers);
    trackers.forEach(function (trackerId) {
        var _a, _b;
        // Store the configuration for this tracker, if it doesn't already exist
        // This allows us to enable click tracking for a tracker if it has been disabled
        _listeners[trackerId] = function (event) {
            eventHandler(event, trackerId, filterFunctionFromFilter(configuration.filter), configuration.defaultLabel, configuration.context);
        };
        var addClickListener = function () {
            document.addEventListener('click', _listeners[trackerId], true);
        };
        if ((_a = _trackers[trackerId]) === null || _a === void 0 ? void 0 : _a.sharedState.hasLoaded) {
            // the load event has already fired, add the click listeners now
            addClickListener();
        }
        else {
            // defer until page has loaded
            (_b = _trackers[trackerId]) === null || _b === void 0 ? void 0 : _b.sharedState.registeredOnLoadHandlers.push(addClickListener);
        }
    });
}
/**
 * Disable automatic click tracking for all `<button>` and `<input type="button">` elements on the page
 *
 * Can be re-enabled with {@link enableButtonClickTracking}
 */
function disableButtonClickTracking(trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    trackers.forEach(function (trackerId) {
        if (_listeners[trackerId]) {
            document.removeEventListener('click', _listeners[trackerId], true);
        }
    });
}
/**
 * Handle a click event
 *
 * @param event - The click event
 * @param trackerId - The tracker identifier which the event will be sent to
 * @param filter - The filter function to use for button click tracking
 * @param context - The dynamic context which will be evaluated for each button click event
 */
function eventHandler(event, trackerId, filter, defaultLabel, context) {
    var elem = (event.composed ? event.composedPath()[0] : event.target);
    while (elem) {
        if (elem instanceof HTMLButtonElement || (elem instanceof HTMLInputElement && elem.type === 'button')) {
            if (filter(elem)) {
                var buttonClickEvent = createEventFromButton(elem, defaultLabel);
                buttonClickEvent.context = resolveDynamicContext(context, buttonClickEvent, elem);
                trackButtonClick(buttonClickEvent, [trackerId]);
            }
            // presume nested buttons aren't a thing
            return;
        }
        elem = elem.parentElement;
    }
}

export { ButtonClickTrackingPlugin, disableButtonClickTracking, enableButtonClickTracking, trackButtonClick };
//# sourceMappingURL=index.module.js.map
